import React, { useState, useEffect, useMemo } from 'react';
import PageHeading from '../../components/PageHeading';
import { SearchOutlined, EditOutlined, DeleteOutlined, ClockCircleOutlined } from '@ant-design/icons';
import axiosInstance from '../../utils/axios';
import { Button, Input, message, Popconfirm, Select, Space, Table } from 'antd';
import { dateFormat } from '../../utils/dateFormats';
import DistrictModal from './DistrictModal';

const District = () => {
    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [selectedState, setSelectedState] = useState(null);

    useEffect(() => {
        fetchStates();
    }, []);

    useEffect(() => {
        if (selectedState !== null)
            fetchDistricts();
    }, [selectedState]);

    const openModalUpdate = (id) => {
        setSelectedId(id);
        setCreateModalOpen(true);
    };

    const openModalCreate = () => {
        setSelectedId(null);
        setCreateModalOpen(true);
    };

    const fetchDistricts = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`core/district/?state_id=${selectedState}`);
            setDistricts(response.data);
        } catch (error) {
            message.error("Failed to fetch districts");
        } finally {
            setLoading(false);
        }
    };

    const fetchStates = async () => {
        try {
            const response = await axiosInstance.get("core/state/");
            setStates(response.data);
        } catch (error) {
            message.error("Failed to fetch states");
        } 
    }

    const cancel = () => message.info('Cancelled');

    const confirm = async (id) => {
        try {
            await axiosInstance.delete(`core/district/${id}/`);
            message.success("Deleted successfully");
            setDistricts(prev => prev.filter(item => item.id !== id));
        } catch (err) {
            message.error("Cannot delete");
        }
    };

    const handleStateChange = (value) => {
        setSelectedState(value)
    }   

    const filteredDistricts = useMemo(() => {
        return districts.filter(item =>
            item.name.toLowerCase().includes(search.toLowerCase())
        );
    }, [search, districts]);

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Created at',
            dataIndex: 'created_at',
            key: 'created_at',
            render: text => dateFormat(text),
        },
        {
            title: 'Action',
            dataIndex: '',
            key: 'actions',
            render: (_, record) => record.restricted ? null : (
                <>
                    <Button type="link" icon={<EditOutlined />}  onClick={() => openModalUpdate(record.id)} />
                    <Popconfirm
                        title="Delete"
                        description="Are you sure to delete this item?"
                        onConfirm={() => confirm(record.id)}
                        onCancel={cancel}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </>
            )
        },
    ];

    return (
        <div className='dashboard'>
            <PageHeading name="Districts" icon={<ClockCircleOutlined />} />

            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16
            }}>
                <Space wrap>
                    <Input
                        placeholder="Search here"
                        prefix={<SearchOutlined />}
                        style={{ width: "190px" }}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <Select
                        placeholder="Select a state"
                        style={{ width: 150 }}
                        onChange={handleStateChange}
                        options={states.map(state => ({ label: state.name, value: state.id }))}
                    />
                </Space>

                <Button type="primary" onClick={openModalCreate}>Add new</Button>
            </div>

            <Table
                dataSource={filteredDistricts}
                loading={loading}
                columns={columns}
                rowKey="id"
                pagination
            />

            <DistrictModal
                open={createModalOpen}
                setOpen={setCreateModalOpen}
                states={states}
                setStates={setStates}
                selectedId={selectedId}
                districts={districts}
                setDistricts={setDistricts}
                selectedState={selectedState}
            />
        </div>
    )
}

export default District